import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t } from '../i18n.js';
import { ICOON } from './iconen.jsx';

/**
 * Verwachte poorten (docs/besluiten/verwachte-poorten.md): een open poort die zo hoort, met de reden. Een nieuwe open
 * poort die hier staat, is geen alert; bij Scans staat de reden bij de poort. Zonder adres of reeks: op elk adres.
 */
export default function VerwachtePoorten() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['verwachte-poorten'], queryFn: api.verwachtePoorten });
  const leeg = { cidr: '', port: '', protocol: 'tcp', reason: '' };
  const [nieuw, zetNieuw] = useState(leeg);
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const zet = (veld) => (e) => { const w = e.target.value; zetNieuw((x) => ({ ...x, [veld]: w })); };
  const doe = async (fn) => {
    if (bezig) return;
    zetMelding(null); zetBezig(true);
    try {
      const m = await fn();
      if (m) zetMelding({ goed: true, tekst: m });
      await q.refetch();
      client.invalidateQueries({ queryKey: ['scans'] }); client.invalidateQueries({ queryKey: ['alerts'] });
    } catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); } finally { zetBezig(false); }
  };

  return (
    <section className="card">
      <header><h2>{t('Verwachte poorten')}</h2></header>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : q.data.data.length ? (
        <table>
          <thead><tr><th>{t('Poort')}</th><th>{t('Waar')}</th><th>{t('Waarom')}</th><th /></tr></thead>
          <tbody>{q.data.data.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.port}/{r.protocol}</td>
              <td className="mono">{r.cidr ?? <span className="muted" style={{ fontFamily: 'inherit' }}>{t('elk adres')}</span>}</td>
              <td>{r.reason}{r.createdBy && <><br /><span className="muted" style={{ fontSize: 12 }}>{r.createdBy}</span></>}</td>
              <td style={{ textAlign: 'right' }}><button type="button" className="icoonknop" title={t('Verwijderen')} aria-label={t('Verwijderen')}
                onClick={() => doe(async () => { await api.verwijderVerwachtePoort(r.id); })}>{ICOON.prullenbak}</button></td>
            </tr>
          ))}</tbody>
        </table>
      ) : <p className="empty">{t('Nog geen: elke nieuwe open poort is een alert.')}</p>}
      <form className="form" style={{ marginTop: 10 }} onSubmit={(e) => {
        e.preventDefault();
        doe(async () => {
          const r = (await api.maakVerwachtePoort({ cidr: nieuw.cidr.trim(), port: Number(nieuw.port), protocol: nieuw.protocol, reason: nieuw.reason })).data;
          zetNieuw(leeg);
          return r.closedAlerts ? t('Toegevoegd; {n} alert(s) over deze poort afgesloten met deze reden.', { n: r.closedAlerts }) : t('Toegevoegd.');
        });
      }}>
        <div className="rij" style={{ gap: 10, alignItems: 'flex-end' }}>
          <label className="field" style={{ width: 100 }}><span>{t('Poort')}</span>
            <input className="input mono" inputMode="numeric" value={nieuw.port} onChange={(e) => zetNieuw((x) => ({ ...x, port: e.target.value.replace(/\D/g, '') }))} placeholder="22" /></label>
          <label className="field" style={{ width: 90 }}><span>{t('Protocol')}</span>
            <select className="input" value={nieuw.protocol} onChange={zet('protocol')}><option value="tcp">tcp</option><option value="udp">udp</option></select></label>
          <label className="field" style={{ flex: 1, minWidth: 160 }}><span>{t('Adres of reeks (leeg: elk adres)')}</span>
            <input className="input mono" value={nieuw.cidr} onChange={zet('cidr')} placeholder="188.122.86.0/24" /></label>
        </div>
        <label className="field"><span>{t('Waarom staat hij open?')}</span>
          <input className="input" value={nieuw.reason} onChange={zet('reason')} placeholder={t('Bijvoorbeeld: SSH alleen open vanaf 188.122.86.165, voor het beheer door ADTS')} /></label>
        <div className="actions"><button className="btn primary" disabled={bezig || !nieuw.port || nieuw.reason.trim().length < 3}>{t('Verwachte poort toevoegen')}</button></div>
        <p className="noot">{t('Een verwachte poort die er nieuw bij komt, is geen alert. Een open alert waarvan elke poort nu verwacht is, sluit ADTS ZERO af met deze reden. Bij Scans staat de reden bij de poort.')}</p>
      </form>
    </section>
  );
}
